import { h, Component } from 'preact';
import Store from 'store/store';
import { showCreateShareLinkModal } from 'utils/createShareLinkModal';
import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { getSimplePreviewPageLink, preparePreviewData } from 'utils/preview';
import { isPowsterEmployee } from 'utils/roles';
import { getThundrData } from 'utils/thundr';

import Button from 'components/button/button';

export default class PreviewButton extends Component {
	get isMoviePage() {
		return this.props.path.includes('/movies/');
	}

	constructor(props) {
		super(props);

		this.state = {
			thundrData: null,
			localPreviewConfirmed: false,
		};

		this.onPreview = this.onPreview.bind(this);
		this.onLocalPreview = this.onLocalPreview.bind(this);
		this.onCreateShareLink = this.onCreateShareLink.bind(this);
	}

	componentDidMount() {
		this.loadThundrData();
	}

	async loadThundrData() {
		if (!this.isMoviePage) return;
		const data = jsonPointer.get(Store.get().data, this.props.path);
		const thundrData = await getThundrData(data);
		this.setState({ thundrData });
	}

	onPreview() {
		const data = jsonPointer.get(Store.get().data, this.props.path);
		preparePreviewData(data);
	}

	async onLocalPreview(e) {
		e.preventDefault(); // Prevent the link from opening immediately

		if (!this.state.localPreviewConfirmed) {
			// Confirm the user has the app running locally before opening the link
			const res = await confirm('Please ensure you have the app running locally from thundr before proceeding.', {
				title: 'Local preview',
				button: 'Proceed',
				buttonIconAlt: 'share',
			});
			if (!res) return;
			this.setState({ localPreviewConfirmed: true });
		}

		this.onPreview(); // Prepare preview data before opening the link

		const linkElement = e.target.closest('a');
		window.open(linkElement.href, linkElement.target); // Open the link
	}

	onCreateShareLink() {
		showCreateShareLinkModal({ path: this.props.path });
	}

	renderMobilePreviewButton() {
		return (
			<Button
				icon="mobile"
				href={getSimplePreviewPageLink(this.props.path, 'mobile')}
				target="preview"
				onClick={this.onPreview}
			>
				Mobile preview
			</Button>
		);
	}

	renderDemoPreviewButton() {
		return (
			<Button
				icon="desktop"
				href={getSimplePreviewPageLink(this.props.path, 'demo')}
				target="preview"
				onClick={this.onPreview}
			>
				Demo preview
			</Button>
		);
	}

	renderLocalPreviewButton() {
		return (
			<Button
				icon="edit"
				href={getSimplePreviewPageLink(this.props.path, 'local')}
				target="preview"
				onClick={this.onLocalPreview}
			>
				Local preview
			</Button>
		);
	}

	renderSharePreviewButton() {
		return (
			<Button
				icon="upload"
				onClick={this.onCreateShareLink}
			>
				Share preview
			</Button>
		);
	}

	render(props, state) {
		const text = props.children?.length ? props.children : 'Preview';
		const disabled = !props.path;

		// Only show the local preview button if the user is a Powster employee, thundr id exists, and the option isn't hidden
		const data = jsonPointer.get(Store.get().data, this.props.path);
		const allowLocalPreview = !props.hideLocalPreview && data.thundr_id && isPowsterEmployee();
		const allowDemoPreview = !props.hideDemoPreview && data.thundr_id && data.ready_state !== 'none' && isPowsterEmployee();

		const additionalOptions = [
			allowLocalPreview && this.renderLocalPreviewButton(),
			allowDemoPreview && this.renderDemoPreviewButton(),
			!props.hideMobilePreview && this.renderMobilePreviewButton(),
			!props.hideSharePreview && this.renderSharePreviewButton()
		].filter(Boolean);

		return (
			<Button
				outline
				dark={props.dark}
				icon="share"
				href={getSimplePreviewPageLink(props.path)}
				target="preview"
				disabled={disabled}
				onClick={this.onPreview}
			>
				{text}
				{!!additionalOptions.length && (
					<div popover="auto" id="editor-preview">
						{additionalOptions}
					</div>
				)}
			</Button>
		);
	}
}